<template>
    <TransitionRoot as="template" :show="dialog">
          <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="$emit('onClose')">
                <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                   <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                      <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                   </TransitionChild>
 
                   <!-- This element is to trick the browser into centering the modal contents. -->
                   <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                   <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                      <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div class="sm:flex sm:items-start">
                               <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-[#F3F4F6] sm:mx-0 sm:h-10 sm:w-10">
                                  <PlusIcon class="w-6 h-6 text-primary--800" aria-hidden="true" />
                               </div>
                               <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                  <DialogTitle as="h3" class="text-lg font-bold leading-6 text-primary--800">
                                         {{ title }}
                                  </DialogTitle>
                                  <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                           <!-- Are your sure you wish to import this file? -->
                                        </p>
                                  </div>
                               </div>
                            </div>
                            <div class="mt-5">
                                <div class="rounded-md img-upload__wrap">
                                    <div class="upload-wrap">
                                        <img class="block mx-auto mb-4" src="/assets/images/img-placeholder.svg">
                                        <h5>Select a file <span class="text-gray-400">or drag and drop</span></h5>
                                        <label for="addon-upload">
                                            <span class="text-sm text-gray-400" v-for="(fn,index) in file" :key="index">
                                                {{fn.name}} 
                                                <span v-if="index != file.length - 1">, </span>
                                            </span>

                                            <span class="text-xs text-gray-500" v-if="file.length === 0">
                                                Max File Size: 10MB | File Types: PNG, JPEG, JPG
                                            </span>

                                            <input 
                                                type="file" 
                                                @change="handleFileUpload" 
                                                :name="'file_upload'" 
                                                accept="image/png, image/JPEG, image/JPG," 
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <button 
                                    type="button" 
                                    :disabled="loader"
                                    class="inline-flex justify-center w-full px-4 py-2 font-medium text-white rounded-md shadow-sm bg-primary-500 sm:ml-3 sm:w-auto sm:text-sm border-primary-500 disabled:opacity-25 focus:ring-1 focus:ring-offset-1" 
                                    @click="submitFile"
                                >
                                    Upload
                                </button>
                                <button 
                                    type="button" 
                                    class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm bg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm" 
                                    @click="$emit('onClose')" ref="cancelButtonRef"
                                >
                                    Cancel
                                </button>
                            </div>
                      </div>
                   </TransitionChild>
                </div>
          </Dialog>
    </TransitionRoot>

    <PageLoader v-if="loader" />

    <ErrorModal
        width="w-[548px]"
        title="Error"
        action-text="Close"
        cancel-text="Go back"
        :show="showError"
        @close="showError = false"
        @confirm="showError = false"
        >
        <template #content>
            {{ errorMsg }}
        </template>
    </ErrorModal>

    <SuccessModal
        :show="showSuccess"
        @cancel="showSuccess = false"
        @confirm="showSuccess = false"
        action-text="Close"
        :title="`${title} Upload`"
        content="Upload success!"
    />
    
 </template>
 
 <script setup lang="ts">
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import http from '@/axios'
import { ref } from "vue";
import { PlusIcon } from "@heroicons/vue/24/outline";
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import AuthService from "@/classes/AuthService";

const TOKEN_PARAM_STRING = AuthService.getHybrainTokenQueryString();

 import Storage from '@/helpers/storage';

 const storage = new Storage();

const emit = defineEmits<{
    ( e: 'onClose', val: any): void
}>()

const props = defineProps({
    title: {
        default: '',
        type: String
    },
    type: {
        type: String
    },
    patientId: {
        type: Number
    },
    dialog: {
        default: false,
        type: Boolean
    },
    importRoute: {
        default: null,
        type: String
    },
});

const file         = ref(['image/*']);
const loader      = ref(false);
const showError   = ref(false);
const errorMsg    = ref(null);
const showSuccess = ref(false);

const handleFileUpload = (event: any) : void => {
    file.value = event.target.files
}

const submitFile = async () => {
    loader.value = true;

    const formData = new FormData()
    
    formData.append('file', file.value[0])

    const route = `${props.importRoute}?patient_id=${props.patientId}&type=${props.type}&${TOKEN_PARAM_STRING}`

    try {
        const {data} = await http.post(route, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        const image = data.response.image
        const patient = JSON.parse(storage.get('patient'));

        if (props.type == 'sr_id') {
            patient.senior_citizen_id_base_64 = image;
        } else if (props.type == 'pwd_id') {
            patient.pwd_id_base_64 = image;
        }

        storage.set('patient', JSON.stringify(patient))

        loader.value = false;
        showSuccess.value = true

        setTimeout(() => window.location.reload(), 1000);

    } catch ({response: {status, data: {message}} = {}}) {
        loader.value = false;
        showError.value = true;
        errorMsg.value = message;

        if(status === 401) {
            window.location.replace('/login');
        }
    }
}
 </script>

<style lang="scss" scoped>
.img-upload__wrap {
    border: 2px dashed #BFC7D1;
    padding: 2rem 1.375rem;
    box-sizing: border-box;
    max-width: 100%;
    margin: auto;
    position: relative;
    z-index: 1;
    .upload-wrap {
        text-align: center;
        label {
            font-size: 12px;
            line-height: 16px;
            color: #6E8197;
            input {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                opacity: 0;
                cursor: pointer;
            }
        }
    }
    .image-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 1rem;
        .uploaded-image {
            max-width: 100%;
            height: auto;
            margin: auto;
        }
        .remove-image {
            cursor: pointer;
            color: #cc0000;
        }
    }
}
</style>
 
 